import {
  ConferenceDetails,
  ParticipantDetail,
  SurveyDetail,
  SurveyParticipantResponse,
  DownloadDetail,
  DownloadClick
} from '@/models/DCPConference';

// Hilfstypen für die Transformation
interface SessionSurveyEntry {
  surveyId: string;
  response: SurveyParticipantResponse;
}

interface SessionDownloadEntry {
  downloadKey: string | number | null;
  click: DownloadClick;
}

/**
 * Ergänzt User-basierte Conference-Daten mit Informationen aus den Sessions.
 * Fügt leftAt (letzte Session), ipAddress und userAgent (erste Session) hinzu.
 *
 * @param conferenceDetails Original Conference-Daten vom Server
 * @returns Conference-Daten mit ergänzten Session-Informationen
 */
export function enrichUserBasedData(conferenceDetails: ConferenceDetails): ConferenceDetails {
  const enrichedParticipants: Record<string, ParticipantDetail> = {};

  Object.entries(conferenceDetails.participants ?? {}).forEach(([participantId, participant]) => {
    const sessions = participant.sessions ?? [];

    // Letzte Session für leftAt
    const lastSession = sessions.length > 0 ? sessions[sessions.length - 1] : null;

    // Erste Session für IP und UserAgent
    const firstSession = sessions.length > 0 ? sessions[0] : null;

    enrichedParticipants[participantId] = {
      ...participant,
      leftAt: lastSession?.leftAt,
      ipAddress: firstSession?.ipAddress ?? participant.ipAddress,
      userAgent: firstSession?.userAgent ?? participant.userAgent
    };
  });

  return {
    ...conferenceDetails,
    participants: enrichedParticipants
  };
}

/**
 * Transformiert User-basierte Conference-Daten in Session-basierte Daten.
 * Jede Session eines Users wird zu einem eigenen "virtuellen Teilnehmer".
 * Survey-Antworten und Downloads werden anhand des Zeitstempels der jeweiligen Session zugeordnet.
 *
 * @param conferenceDetails Original Conference-Daten vom Server
 * @returns Transformierte Conference-Daten mit Session-basierten Teilnehmern
 */
export function transformToSessionBasedData(conferenceDetails: ConferenceDetails): ConferenceDetails {
  const sessionBasedParticipants: Record<string, ParticipantDetail> = {};
  const sessionSurveyMap = new Map<string, SessionSurveyEntry[]>(); // sessionId -> survey responses
  const sessionDownloadMap = new Map<string, SessionDownloadEntry[]>(); // sessionId -> download clicks

  // Durchlaufe alle Teilnehmer und ihre Sessions
  Object.values(conferenceDetails.participants ?? {}).forEach(participant => {
    participant.sessions?.forEach((session, sessionIndex) => {
      const sessionStart = new Date(session.joinedAt).getTime();
      const sessionEnd = new Date(session.leftAt).getTime();
      const sessionDuration = Math.floor((sessionEnd - sessionStart) / 1000);

      const sessionId = `${participant.participantId}_s${sessionIndex + 1}`;
      const originalUserId = participant.participantId;

      // Erstelle virtuellen Teilnehmer für diese Session
      sessionBasedParticipants[sessionId] = {
        participantId: sessionId,
        name: participant.name,
        email: participant.email,
        firstJoinTime: session.joinedAt,
        leftAt: session.leftAt,
        totalDurationSeconds: sessionDuration,
        durationWaitingRoomSeconds: session.durationWaitingRoomSeconds ?? 0,
        durationParticipantSeconds: session.durationParticipantSeconds ?? 0,
        durationCoModeratorSeconds: session.durationCoModeratorSeconds ?? 0,
        ipAddress: session.ipAddress,
        userAgent: session.userAgent,
        urlParameters: participant.urlParameters,
        sessions: [] // Sessions werden nicht weiter verschachtelt
      };

      // Sammle Survey-Antworten für diese Session
      conferenceDetails.surveys?.forEach(survey => {
        const originalResponse = survey.participantResponses?.[originalUserId];
        if (originalResponse) {
          const answeredAt = new Date(originalResponse.answeredAt).getTime();

          // Prüfe ob die Antwort innerhalb dieser Session erfolgte
          if (answeredAt >= sessionStart && answeredAt <= sessionEnd) {
            if (!sessionSurveyMap.has(sessionId)) {
              sessionSurveyMap.set(sessionId, []);
            }

            // Erstelle neue Response mit Session-ID als userId
            const sessionResponse: SurveyParticipantResponse = {
              ...originalResponse,
              userId: sessionId
            };

            sessionSurveyMap.get(sessionId)!.push({
              surveyId: survey.surveyId,
              response: sessionResponse
            });
          }
        }
      });

      // Sammle Downloads für diese Session
      conferenceDetails.downloads?.forEach(download => {
        download.clicks?.forEach(click => {
          if (click.userId === originalUserId) {
            const clickedAt = new Date(click.clickedAt).getTime();

            // Prüfe ob der Download innerhalb dieser Session erfolgte
            if (clickedAt >= sessionStart && clickedAt <= sessionEnd) {
              if (!sessionDownloadMap.has(sessionId)) {
                sessionDownloadMap.set(sessionId, []);
              }

              const downloadKey = download.downloadUuid || download.downloadId;
              sessionDownloadMap.get(sessionId)!.push({
                downloadKey,
                click: { ...click, userId: sessionId }
              });
            }
          }
        });
      });
    });
  });

  // Baue Surveys mit Session-IDs neu auf
  const sessionBasedSurveys: SurveyDetail[] = conferenceDetails.surveys?.map(survey => {
    const filteredResponses: Record<string, SurveyParticipantResponse> = {};

    // Durchlaufe alle Sessions und ihre Survey-Antworten
    sessionSurveyMap.forEach((entries, sessionId) => {
      entries.forEach(entry => {
        if (entry.surveyId === survey.surveyId) {
          filteredResponses[sessionId] = entry.response;
        }
      });
    });

    return {
      ...survey,
      participantResponses: filteredResponses
    };
  }) ?? [];

  // Baue Downloads mit Session-IDs neu auf
  const sessionBasedDownloads: DownloadDetail[] = conferenceDetails.downloads?.map(download => {
    const filteredClicks: DownloadClick[] = [];
    const downloadKey = download.downloadUuid || download.downloadId;

    // Durchlaufe alle Sessions und ihre Downloads
    sessionDownloadMap.forEach((entries, sessionId) => {
      entries.forEach(entry => {
        if (entry.downloadKey === downloadKey) {
          filteredClicks.push(entry.click);
        }
      });
    });

    return {
      ...download,
      clicks: filteredClicks
    };
  }) ?? [];

  return {
    ...conferenceDetails,
    participants: sessionBasedParticipants,
    surveys: sessionBasedSurveys,
    downloads: sessionBasedDownloads
  };
}
